// Pure, dependency-free pre-validation for a Dashboard Spec — mirrors the cheap,
// high-value checks the engine validator runs (cloud-agent
// internal/chat/dashboardspec). This is for fast, friendly UX in the dashboard
// editor (block B6): fail fast on obvious mistakes before a round-trip. It is
// deliberately NOT a full JSON-Schema validator — the engine (santhosh-tekuri
// against ./schema.json) is the authoritative safety net. Keep it consistent
// with providers.ts's pure-validate philosophy.

import { DASHBOARD_SCHEMA_VERSION, type DashboardSpec, type PanelType } from './types.js';

/** One structural problem, shaped like the engine's SpecError. */
export interface SpecError {
  /** JSON-pointer-ish path into the spec, e.g. "/panels/0/binding". */
  path: string;
  /** Short failing-rule tag, e.g. "required", "enum", "integrity.layout". */
  keyword: string;
  message: string;
}

export interface ValidateResult {
  ok: boolean;
  errors: SpecError[];
}

const PANEL_TYPES: ReadonlySet<string> = new Set<PanelType>([
  'kpi', 'gauge', 'table', 'bar', 'line', 'area', 'point', 'arc', 'rule', 'tick', 'rect',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * The result-column key a measure term produces: "[Name]" for a catalog ref
 * (string form), "[as]" for an inline aggregate (object form). Returns undefined
 * for a malformed term (the engine schema is the authority that rejects it).
 */
function measureColumnKey(term: unknown): string | undefined {
  if (typeof term === 'string') return term;
  if (isObject(term) && nonEmptyString(term.as)) return `[${term.as}]`;
  return undefined;
}

/**
 * Validates the envelope + cross-reference integrity of a candidate spec. Returns
 * `{ ok, errors }`; `ok` is true only when `errors` is empty. Never throws.
 */
export function validateDashboardSpec(input: unknown): ValidateResult {
  const errors: SpecError[] = [];
  const push = (path: string, keyword: string, message: string) => errors.push({ path, keyword, message });

  if (!isObject(input)) {
    return { ok: false, errors: [{ path: '', keyword: 'type', message: 'spec must be a JSON object' }] };
  }

  if (input.schema !== DASHBOARD_SCHEMA_VERSION) {
    push('/schema', 'const', `schema must be "${DASHBOARD_SCHEMA_VERSION}"`);
  }
  if (!nonEmptyString(input.title)) {
    push('/title', 'required', 'title is required and must be a non-empty string');
  }
  if (!isObject(input.scope) || !nonEmptyString(input.scope.repoId)) {
    push('/scope', 'required', 'scope.repoId is required');
  }

  const layout = Array.isArray(input.layout) ? input.layout : [];
  if (layout.length === 0) {
    push('/layout', 'minItems', 'layout must have at least one entry');
  }
  const panels = Array.isArray(input.panels) ? input.panels : [];
  if (panels.length === 0) {
    push('/panels', 'minItems', 'panels must have at least one panel');
  }

  // Per-panel envelope checks.
  const panelIds = new Map<string, number>();
  panels.forEach((p, i) => {
    if (!isObject(p)) {
      push(`/panels/${i}`, 'type', 'panel must be an object');
      return;
    }
    if (!nonEmptyString(p.id)) {
      push(`/panels/${i}/id`, 'required', 'panel id is required');
    } else {
      if (panelIds.has(p.id)) push(`/panels/${i}/id`, 'integrity.duplicateId', `duplicate panel id "${p.id}"`);
      panelIds.set(p.id, i);
    }
    if (typeof p.type !== 'string' || !PANEL_TYPES.has(p.type)) {
      push(`/panels/${i}/type`, 'enum', `panel type "${String(p.type)}" is not a known type`);
    }
    if (!nonEmptyString(p.title)) push(`/panels/${i}/title`, 'required', 'panel title is required');
    if (!isObject(p.viz)) push(`/panels/${i}/viz`, 'required', 'panel viz is required');

    // Exactly one of binding / compositeBinding (the schema oneOf).
    const hasBinding = isObject(p.binding);
    const hasComposite = isObject(p.compositeBinding);
    if (!hasBinding && !hasComposite) {
      push(`/panels/${i}/binding`, 'required', 'panel needs a binding or a compositeBinding');
    } else if (hasBinding && hasComposite) {
      push(`/panels/${i}`, 'oneOf', 'panel must carry exactly one of binding / compositeBinding');
    }
    if (hasBinding) {
      validateBinding(p.binding as Record<string, unknown>, `/panels/${i}/binding`, push);
    }
    if (hasComposite) {
      validateComposite(p.compositeBinding as Record<string, unknown>, `/panels/${i}/compositeBinding`, push);
    }
  });

  // Integrity 1: id <-> layout 1:1.
  const layoutIds = new Map<string, number>();
  layout.forEach((l, i) => {
    if (!isObject(l) || !nonEmptyString(l.i)) {
      push(`/layout/${i}/i`, 'required', 'layout entry needs a string id "i"');
      return;
    }
    if (layoutIds.has(l.i)) push(`/layout/${i}/i`, 'integrity.duplicateLayout', `duplicate layout entry for id "${l.i}"`);
    layoutIds.set(l.i, i);
    if (!panelIds.has(l.i)) push(`/layout/${i}/i`, 'integrity.layout', `layout references id "${l.i}" with no matching panel`);
  });
  for (const [id, i] of panelIds) {
    if (!layoutIds.has(id)) push(`/panels/${i}`, 'integrity.layout', `panel "${id}" has no layout entry`);
  }

  // Integrity 2: param references resolve.
  const paramIds = new Set<string>();
  if (Array.isArray(input.parameters)) {
    for (const p of input.parameters) {
      if (isObject(p) && nonEmptyString(p.id)) paramIds.add(p.id);
    }
  }
  panels.forEach((p, pi) => {
    if (!isObject(p)) return;
    const b = isObject(p.binding) ? p.binding : undefined;
    const filters = b && Array.isArray(b.filters) ? b.filters : [];
    filters.forEach((f, fi) => {
      if (isObject(f) && typeof f.param === 'string' && f.param.length > 0 && !paramIds.has(f.param)) {
        push(`/panels/${pi}/binding/filters/${fi}/param`, 'integrity.paramRef', `filter references undeclared parameter "${f.param}"`);
      }
    });
    const cb = isObject(p.compositeBinding) ? p.compositeBinding : undefined;
    const sources = cb && Array.isArray(cb.sources) ? cb.sources : [];
    sources.forEach((s, si) => {
      if (!isObject(s) || !isObject(s.binding)) return;
      const sFilters = Array.isArray((s.binding as Record<string, unknown>).filters)
        ? ((s.binding as Record<string, unknown>).filters as unknown[])
        : [];
      sFilters.forEach((f, fi) => {
        if (isObject(f) && typeof f.param === 'string' && f.param.length > 0 && !paramIds.has(f.param)) {
          push(`/panels/${pi}/compositeBinding/sources/${si}/binding/filters/${fi}/param`, 'integrity.paramRef',
            `filter references undeclared parameter "${f.param}"`);
        }
      });
    });
    const inter = isObject(p.interaction) ? p.interaction : undefined;
    const emits = inter && Array.isArray(inter.emits) ? inter.emits : [];
    emits.forEach((e, ei) => {
      if (isObject(e) && typeof e.param === 'string' && e.param.length > 0 && !paramIds.has(e.param)) {
        push(`/panels/${pi}/interaction/emits/${ei}/param`, 'integrity.paramRef', `interaction emits undeclared parameter "${e.param}"`);
      }
    });
  });

  return { ok: errors.length === 0, errors };
}

type PushFn = (path: string, keyword: string, message: string) => void;

/** The shared single-model binding checks (plain panels and composite sources). */
function validateBinding(b: Record<string, unknown>, base: string, push: PushFn): void {
  if (!Array.isArray(b.measures) || b.measures.length === 0) {
    push(`${base}/measures`, 'minItems', 'binding.measures must be a non-empty array');
  } else {
    // Integrity 3: measure-term result keys unique within a binding (a duplicate
    // measure ref or inline-aggregate alias would collide in the compiled output).
    const seenKeys = new Set<string>();
    b.measures.forEach((m, mi) => {
      const key = measureColumnKey(m);
      if (key === undefined) return; // malformed term; the engine schema rejects it
      const norm = key.toLowerCase();
      if (seenKeys.has(norm)) {
        push(`${base}/measures/${mi}`, 'integrity.duplicateMeasure',
          `measure term "${key}" duplicates an earlier term's result column in this binding`);
      }
      seenKeys.add(norm);
    });
  }
  // grain is OPTIONAL (WP3a): when present it must be a non-empty string, but an
  // omitted grain is valid and derived engine-side. A column-name grain is
  // normalized (not rejected) by the engine bind-check.
  if ('grain' in b && b.grain !== undefined && !nonEmptyString(b.grain)) {
    push(`${base}/grain`, 'type', 'binding.grain, when present, must be a non-empty string (an entity localKey or "total")');
  }
}

/**
 * The catalog-free compositeBinding checks — mirrors the engine's composite
 * integrity rules (roles distinct, 'on' iff visual grain, alias uniqueness,
 * output-mode coherence). Link existence, key fields, PII and the expression
 * grammar are engine-side.
 */
function validateComposite(cb: Record<string, unknown>, base: string, push: PushFn): void {
  if (!nonEmptyString(cb.link)) {
    push(`${base}/link`, 'required', 'compositeBinding.link is required (the cross-dataset link name)');
  }
  const sources = Array.isArray(cb.sources) ? cb.sources : [];
  if (sources.length !== 2) {
    push(`${base}/sources`, 'minItems', 'compositeBinding needs exactly two sources');
  }
  const roles = new Set<string>();
  sources.forEach((s, si) => {
    if (!isObject(s)) {
      push(`${base}/sources/${si}`, 'type', 'source must be an object');
      return;
    }
    if (!nonEmptyString(s.role) || !/^[a-z][a-z0-9_]{0,23}$/.test(s.role)) {
      push(`${base}/sources/${si}/role`, 'pattern', 'source role must be a lowercase identifier (max 24 chars)');
    } else if (roles.has(s.role)) {
      push(`${base}/sources/${si}/role`, 'integrity.role', `source roles must be distinct (both "${s.role}")`);
    } else {
      roles.add(s.role);
    }
    if (!isObject(s.binding)) {
      push(`${base}/sources/${si}/binding`, 'required', 'source binding is required');
      return;
    }
    validateBinding(s.binding as Record<string, unknown>, `${base}/sources/${si}/binding`, push);
    if (cb.joinGrain === 'entity' && Array.isArray((s.binding as Record<string, unknown>).dimensions)
      && ((s.binding as Record<string, unknown>).dimensions as unknown[]).length > 0) {
      push(`${base}/sources/${si}/binding/dimensions`, 'integrity.joinOn',
        'entity-grain sources must not author dimensions — the engine injects the link keys');
    }
  });

  const on = Array.isArray(cb.on) ? cb.on : [];
  if (cb.joinGrain === 'visual' && on.length === 0) {
    push(`${base}/on`, 'integrity.joinOn', 'joinGrain "visual" requires at least one on pair');
  } else if (cb.joinGrain === 'entity' && on.length > 0) {
    push(`${base}/on`, 'integrity.joinOn', 'joinGrain "entity" joins on the link\'s key pairs — on must be empty');
  } else if (cb.joinGrain !== 'entity' && cb.joinGrain !== 'visual') {
    push(`${base}/joinGrain`, 'enum', 'joinGrain must be "entity" or "visual"');
  }
  if ('join' in cb && cb.join !== undefined && cb.join !== 'inner' && cb.join !== 'left' && cb.join !== 'full') {
    push(`${base}/join`, 'enum', 'join must be inner, left or full');
  }

  // Alias uniqueness across the joined-row namespace (on + carry + derived).
  const aliases = new Set<string>();
  on.forEach((o, oi) => {
    if (!isObject(o) || !nonEmptyString(o.as)) {
      push(`${base}/on/${oi}/as`, 'required', 'every on pair needs a bare alias "as"');
      return;
    }
    const norm = o.as.toLowerCase();
    if (aliases.has(norm)) push(`${base}/on/${oi}/as`, 'integrity.alias', `joined-column alias "${o.as}" is duplicated`);
    aliases.add(norm);
  });
  sources.forEach((s, si) => {
    if (!isObject(s)) return;
    const carry = Array.isArray(s.carry) ? s.carry : [];
    carry.forEach((c, ci) => {
      if (!isObject(c) || !nonEmptyString(c.as)) {
        push(`${base}/sources/${si}/carry/${ci}/as`, 'required', 'every carry field needs a bare alias "as"');
        return;
      }
      if (!nonEmptyString(c.field)) {
        push(`${base}/sources/${si}/carry/${ci}/field`, 'required', 'every carry field needs a field ref');
      }
      const norm = c.as.toLowerCase();
      if (aliases.has(norm)) push(`${base}/sources/${si}/carry/${ci}/as`, 'integrity.alias', `joined-column alias "${c.as}" is duplicated`);
      aliases.add(norm);
    });
  });
  const derived = Array.isArray(cb.derived) ? cb.derived : [];
  derived.forEach((d, di) => {
    if (!isObject(d) || !nonEmptyString(d.as)) {
      push(`${base}/derived/${di}/as`, 'required', 'every derived term needs an alias "as"');
      return;
    }
    if (!nonEmptyString(d.expr)) {
      push(`${base}/derived/${di}/expr`, 'required', 'every derived term needs an expr');
    }
    const norm = d.as.toLowerCase();
    if (aliases.has(norm)) push(`${base}/derived/${di}/as`, 'integrity.alias', `derived alias "${d.as}" duplicates a joined-column alias`);
    aliases.add(norm);
  });

  // Output coherence.
  const out = isObject(cb.output) ? cb.output : undefined;
  if (!out) {
    push(`${base}/output`, 'required', 'compositeBinding.output is required');
    return;
  }
  if (out.mode !== 'rows' && out.mode !== 'aggregate') {
    push(`${base}/output/mode`, 'enum', 'output.mode must be "rows" or "aggregate"');
  }
  if (Array.isArray(out.groupBy) && out.groupBy.length > 0 && out.mode !== 'aggregate') {
    push(`${base}/output/groupBy`, 'integrity.outputMode', 'output.groupBy requires output.mode="aggregate"');
  }
  const select = Array.isArray(out.select) ? out.select : [];
  if (select.length === 0) {
    push(`${base}/output/select`, 'minItems', 'output.select must have at least one term');
  }
  const outAliases = new Set<string>();
  select.forEach((t, ti) => {
    if (!isObject(t) || !nonEmptyString(t.as)) {
      push(`${base}/output/select/${ti}/as`, 'required', 'every output term needs an alias "as"');
      return;
    }
    const norm = t.as.toLowerCase();
    if (outAliases.has(norm)) push(`${base}/output/select/${ti}/as`, 'integrity.alias', `output alias "${t.as}" is duplicated`);
    outAliases.add(norm);
    if (nonEmptyString(t.agg) && out.mode !== 'aggregate') {
      push(`${base}/output/select/${ti}`, 'integrity.outputMode', `output term "${t.as}" aggregates, which requires output.mode="aggregate"`);
    }
  });

  // output.derived: post-aggregation terms share the output alias namespace
  // (expression parsing and ref resolution are engine-side).
  const outDerived = Array.isArray(out.derived) ? out.derived : [];
  outDerived.forEach((d, di) => {
    if (!isObject(d) || !nonEmptyString(d.as)) {
      push(`${base}/output/derived/${di}/as`, 'required', 'every output.derived term needs an alias "as"');
      return;
    }
    if (!nonEmptyString(d.expr)) {
      push(`${base}/output/derived/${di}/expr`, 'required', 'every output.derived term needs an expr');
    }
    const norm = d.as.toLowerCase();
    if (outAliases.has(norm)) push(`${base}/output/derived/${di}/as`, 'integrity.alias', `output.derived alias "${d.as}" duplicates an output column`);
    outAliases.add(norm);
  });
}

/** Convenience type guard: true when `input` passes pre-validation. */
export function isDashboardSpec(input: unknown): input is DashboardSpec {
  return validateDashboardSpec(input).ok;
}
